import PropTypes from 'prop-types';
import { RecordContextProvider, ReferenceField, TextField, useGetOne, useRecordContext, useTranslate } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ServiceCompanyNamesField } from '@/components/global/fields/ServiceCompanyNamesField.jsx';
import { isConfigurationValueFilled } from '@/components/brand/validators/brandValidators.js';
import { DISCOUNT_LEVELS, configurationTextValue } from '@/components/retailChain/configurationDetailValues.js';
import { brandConfigurationDetailsStyles } from '@/themes/commonStyles/brandConfigurationDetails.js';

const StyledConfiguration = styled(Box)(brandConfigurationDetailsStyles);

const textRows = [
    { key: 'forwarderType', label: 'resources.retailChain.fields.forwarderType', fields: ['forwarderType'] },
    { key: 'operators', label: 'resources.retailChain.fields.operators', fields: ['operators'] },
    { key: 'couponSource', label: 'resources.retailChain.fields.couponSource', fields: ['couponSource'] },
    { key: 'depositWithdrawalMethods', label: 'resources.retailChain.fields.depositWithdrawalMethods', fields: ['depositWithdrawalMethods'] },
    { key: 'defaultDepositValue', label: 'resources.retailChain.fields.defaultDepositValue', fields: ['defaultDepositValue'] },
    { key: 'defaultDepositPoints', label: 'resources.retailChain.fields.defaultDepositPoints', fields: ['defaultDepositPoints'] },
    {
        key: 'discountLimits',
        label: 'resources.retailChain.labels.discountLimits',
        fields: DISCOUNT_LEVELS.map(level => `discountLimitL${level}`),
    },
    {
        key: 'discountValues',
        label: 'resources.retailChain.labels.discountValues',
        fields: DISCOUNT_LEVELS.map(level => `discountValueL${level}`),
    },
];

const isOwn = (record, fields) => fields.some(field => isConfigurationValueFilled(record?.[field]));

const ConfigurationSource = ({ own, chainName }) => {
    const translate = useTranslate();
    const label = own
        ? translate('resources.brand.labels.ownConfiguration')
        : translate('resources.brand.labels.inheritedFromChain', { name: chainName || '—' });

    return (
        <Typography variant='body2' className='brand-configuration-details__source'>
            {label}
        </Typography>
    );
};

ConfigurationSource.propTypes = {
    own: PropTypes.bool.isRequired,
    chainName: PropTypes.string,
};

const ConfigurationRow = ({ label, children, own, chainName }) => (
    <Box className='brand-configuration-details__row'>
        <Typography variant='body2' className='brand-configuration-details__label'>
            {label}
        </Typography>
        <Box className='brand-configuration-details__value'>{children}</Box>
        <ConfigurationSource own={own} chainName={chainName} />
    </Box>
);

ConfigurationRow.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    own: PropTypes.bool.isRequired,
    chainName: PropTypes.string,
};

const CompanyNamesValue = ({ ids }) => (
    <RecordContextProvider value={{ serviceCompanyIds: Array.isArray(ids) ? ids : [] }}>
        <ServiceCompanyNamesField />
    </RecordContextProvider>
);

CompanyNamesValue.propTypes = {
    ids: PropTypes.array,
};

const DefaultCompanyValue = ({ id }) => {
    if (!id) {
        return (
            <Typography variant='body2' component='span'>
                —
            </Typography>
        );
    }

    return (
        <RecordContextProvider value={{ defaultServiceCompanyId: id }}>
            <ReferenceField source='defaultServiceCompanyId' reference='service-company' link={false} emptyText='—'>
                <TextField source='name' />
            </ReferenceField>
        </RecordContextProvider>
    );
};

DefaultCompanyValue.propTypes = {
    id: PropTypes.string,
};

export const BrandConfigurationDetails = () => {
    const translate = useTranslate();
    const brand = useRecordContext();
    const { data: retailChain } = useGetOne('retail-chain', { id: brand?.retailChainId }, { enabled: Boolean(brand?.retailChainId) });
    const chainName = retailChain?.name || '';

    const valueFor = (fields, read) => {
        const own = isOwn(brand, fields);
        const source = own ? brand : retailChain;
        return { own, value: read(source) };
    };
    const serviceCompanies = valueFor(['serviceCompanyIds'], record => record?.serviceCompanyIds);
    const defaultServiceCompany = valueFor(['defaultServiceCompanyId'], record => record?.defaultServiceCompanyId);

    return (
        <StyledConfiguration>
            {textRows.map(row => {
                const { own, value } = valueFor(row.fields, record => configurationTextValue(record, row.key, translate));

                return (
                    <ConfigurationRow key={row.key} label={translate(row.label)} own={own} chainName={chainName}>
                        <Typography variant='body2' component='span'>
                            {value}
                        </Typography>
                    </ConfigurationRow>
                );
            })}
            <ConfigurationRow label={translate('resources.retailChain.fields.serviceCompanies')} own={serviceCompanies.own} chainName={chainName}>
                <CompanyNamesValue ids={serviceCompanies.value} />
            </ConfigurationRow>
            <ConfigurationRow label={translate('resources.retailChain.fields.defaultServiceCompany')} own={defaultServiceCompany.own} chainName={chainName}>
                <DefaultCompanyValue id={defaultServiceCompany.value} />
            </ConfigurationRow>
        </StyledConfiguration>
    );
};

BrandConfigurationDetails.propTypes = {};
